import { useEffect, useMemo, useState } from 'react';
import './styles.css';
import { WeekPicker } from '../datePickers/weekPicker/WeekPicker';
import type { ITopData } from '../../models/ITopData';
import {
  useGetDashboardIdQuery,
  useGetRecordsNumberForTopEmployeesQuery,
  useGetRecordsNumberForTopProjectsQuery,
  useGetTopProjectsQuery,
  useGetTopEmployeesQuery,
  useGetTotalTrackedTimeQuery,
  useGetEmployeeTrackedTimeQuery,
  useGetHourlyTableWorkLogLinkQuery,
  useGetClientsRelationStatsRouteQuery,
  useGetDateRangeModeQuery,
  useGetDashboardIdByDateRangeModeQuery,
  useGetTitleQuery,
  useGetTimeTooltipQuery,
  useGetParentBcIdQuery,
  useGetSummaryClientsQuery,
  useGetSummaryCurrenciesQuery,
  useGetMonthlyClientFinanceEnabledQuery,
  useGetMonthlyClientFinanceSummaryQuery,
} from '../../api/summaryApi';
import CustomDropdown from '../../../../components/common/CustomDropdown';
import TopCard from '../topCard/TopCard';
import MoreButton from '../moreButton/MoreButton';
import Totals from '../totals/Totals';
import type { ITotalTrackedTime } from '../../models/ITotalTrackedTime';
import TrackedTimeTable from '../trackedTimeTable/TrackedTimeTable';
import type { IFilter } from '../../models/IFilter';
import { DateFieldNames, filterService } from '../../services/filterService';
import { MonthPicker } from '../datePickers/monthPicker/MonthPicker';
import { DateRangeMode as DateRangeModeEnum } from '../../const/DateRangeMode';
import type { IEmployeeTrackedTime } from '../../models/IEmployeeTrackedTime';
import { SessionStorageItems, SessionStorageService } from '../../services/sessionStorageService';
import UTCTooltip from '../utcTooltip/UTCTooltip';
import { openEmployeeRelationStatsPage } from '../../utils/clientUtils';
import { buildTotalsBreakdown } from '../../utils/summaryDisplayUtils';
import SummaryCurrencySection from '../summaryCurrencySection/SummaryCurrencySection';
import SummaryFooter from '../summaryFooter/SummaryFooter';
import type { ISummaryCurrencyOption } from '../../models/ISummaryCurrencyOption';
import { MonthlyClientFinanceDashboard } from '../monthlyClientFinance/MonthlyClientFinanceDashboard';

type SummaryProps = {
  showAdminFilters?: boolean;
};

type SummaryCurrencyView = {
  currency: ISummaryCurrencyOption;
  employees: ITopData[];
  projects: ITopData[];
  hourlyEmployees: IEmployeeTrackedTime[];
  totals?: ITotalTrackedTime['Usd'];
};

const summaryViewOptions = [
  { value: DateRangeModeEnum.Week, label: 'Weekly' },
  { value: DateRangeModeEnum.Month, label: 'Monthly' },
];

const summaryDateRangeModes = [DateRangeModeEnum.Week, DateRangeModeEnum.Month] as const;

const isDateRangeMode = (value: string): value is DateRangeModeEnum =>
  summaryDateRangeModes.includes(value as DateRangeModeEnum);

const buildTrackedDates = (employees: IEmployeeTrackedTime[]): Date[] =>
  Array.from(
    new Set(employees.flatMap((e) => e.TrackedTime.map((t) => new Date(t.Date).toISOString())))
  )
    .map((dateStr) => new Date(dateStr))
    .sort((a, b) => a.getTime() - b.getTime());

const getCurrencyViewActivityScore = (view: SummaryCurrencyView): number =>
  view.employees.length +
  view.projects.length +
  view.hourlyEmployees.length +
  (view.totals?.TotalPrice || view.totals?.TotalHours ? 1 : 0);

const Summary = ({ showAdminFilters = true }: SummaryProps) => {
  const [useLocalTime] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | undefined>(undefined);
  const [selectedDateRangeMode, setSelectedDateRangeMode] = useState<DateRangeModeEnum | undefined>(
    undefined
  );
  const showPrices = true;

  const { data: summaryCurrencies = [] } = useGetSummaryCurrenciesQuery();

  const usdCurrency = useMemo(
    () => summaryCurrencies.find((c) => c.Code === 'USD'),
    [summaryCurrencies]
  );
  const eurCurrency = useMemo(
    () => summaryCurrencies.find((c) => c.Code === 'EUR'),
    [summaryCurrencies]
  );

  const { data: baseDashboardId } = useGetDashboardIdQuery(window.location.pathname);

  const { data: dateRangeModeData } = useGetDateRangeModeQuery(baseDashboardId!, {
    skip: !baseDashboardId,
  });

  const dateRangeMode = useMemo(() => {
    const fromApi =
      dateRangeModeData && isDateRangeMode(dateRangeModeData) ? dateRangeModeData : undefined;

    return showAdminFilters ? (selectedDateRangeMode ?? fromApi) : fromApi;
  }, [dateRangeModeData, selectedDateRangeMode, showAdminFilters]);

  const { data: dashboardIdByMode } = useGetDashboardIdByDateRangeModeQuery(
    {
      dashboardId: baseDashboardId!,
      dateRangeMode: dateRangeMode ?? DateRangeModeEnum.Month,
    },
    {
      skip: !baseDashboardId || !dateRangeMode,
    }
  );

  const dashboardId = dashboardIdByMode ?? baseDashboardId;
  const [isFinanceView, setIsFinanceView] = useState(false);

  const { data: isMonthlyClientFinanceEnabled = false } = useGetMonthlyClientFinanceEnabledQuery(
    dashboardId!,
    { skip: !dashboardId }
  );

  useEffect(() => {
    if (!dashboardId || !isMonthlyClientFinanceEnabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsFinanceView(false);
      return;
    }

    const storageKey = `summary-finance-view-${dashboardId}`;
    const savedValue = window.sessionStorage.getItem(storageKey);
    // Finance is the initial view for its dedicated dashboard, but a user's
    // Standard/Finance choice remains theirs for the browser session.

    setIsFinanceView(savedValue == null ? true : savedValue === 'true');
  }, [dashboardId, isMonthlyClientFinanceEnabled]);

  const onFinanceViewChange = (enabled: boolean) => {
    setIsFinanceView(enabled);
    if (dashboardId) {
      window.sessionStorage.setItem(`summary-finance-view-${dashboardId}`, String(enabled));
    }
  };

  const getInitialDateRange = (mode: string) => {
    if (mode === DateRangeModeEnum.Week) {
      return SessionStorageService.getDatePickerLastDateRangeOrDefault(
        SessionStorageItems.weekPickerLastDateRange
      );
    }

    if (mode === DateRangeModeEnum.Month) {
      return SessionStorageService.getDatePickerLastDateRangeOrDefault(
        SessionStorageItems.monthPickerLastDateRange
      );
    }

    return {
      firstDay: new Date(),
      lastDay: new Date(),
    };
  };

  const [dateRange, setDateRange] = useState<{ firstDay: Date; lastDay: Date }>(() => ({
    firstDay: new Date(),
    lastDay: new Date(),
  }));

  useEffect(() => {
    if (!dateRangeMode) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDateRange(getInitialDateRange(dateRangeMode));
  }, [dateRangeMode]);

  useEffect(() => {
    if (!dateRangeModeData || selectedDateRangeMode || !isDateRangeMode(dateRangeModeData)) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedDateRangeMode(dateRangeModeData);
  }, [dateRangeModeData, selectedDateRangeMode]);

  const hasValidDateRange = dateRange.firstDay < dateRange.lastDay;
  const shouldLoadGenericSummary = !isFinanceView;

  const dateRangeQuery = {
    start: new Date(dateRange.firstDay),
    end: new Date(dateRange.lastDay),
    useLocalTime,
    clientId: selectedClientId,
  };

  const {
    data: monthlyClientFinance,
    isLoading: isLoadingMonthlyClientFinance,
    isFetching: isFetchingMonthlyClientFinance,
  } = useGetMonthlyClientFinanceSummaryQuery(
    { monthStart: dateRange.firstDay, dashboardId: dashboardId!, clientId: selectedClientId },
    { skip: !dashboardId || !isFinanceView }
  );

  const {
    data: topEmployees,
    isLoading: isLoadingEmployees,
    isFetching: isFetchingEmployees,
  } = useGetTopEmployeesQuery(dateRangeQuery, {
    skip: !hasValidDateRange || !shouldLoadGenericSummary,
  });

  const { data: employeeRecordsCountData } = useGetRecordsNumberForTopEmployeesQuery(dashboardId!, {
    skip: !dashboardId || !shouldLoadGenericSummary,
  });

  const {
    data: topProjects,
    isLoading: isLoadingProjects,
    isFetching: isFetchingProjects,
  } = useGetTopProjectsQuery(dateRangeQuery, {
    skip: !hasValidDateRange || !shouldLoadGenericSummary,
  });

  const { data: projectsRecordsCountData } = useGetRecordsNumberForTopProjectsQuery(dashboardId!, {
    skip: !dashboardId || !shouldLoadGenericSummary,
  });

  const {
    data: totalTrackedTime,
    isLoading: isLoadingTotals,
    isFetching: isFetchingTotals,
  } = useGetTotalTrackedTimeQuery(dateRangeQuery, {
    skip: !hasValidDateRange || !shouldLoadGenericSummary,
  });

  const {
    data: employeeTrackedTime,
    isLoading: isLoadingTrackedTime,
    isFetching: isFetchingTrackedTime,
  } = useGetEmployeeTrackedTimeQuery(
    {
      ...dateRangeQuery,
      dashboardId: dashboardId!,
    },
    { skip: !hasValidDateRange || !dashboardId || !shouldLoadGenericSummary }
  );

  const { data: summaryClients = [] } = useGetSummaryClientsQuery(undefined, {
    skip: !showAdminFilters,
  });

  const { data: hourlyTableWorkLogLinkData } = useGetHourlyTableWorkLogLinkQuery(dashboardId!, {
    skip: !dashboardId || !shouldLoadGenericSummary,
  });

  const { data: clientRelationStatsRouteData } = useGetClientsRelationStatsRouteQuery(
    dashboardId!,
    { skip: !dashboardId || !shouldLoadGenericSummary }
  );

  const { data: titleData } = useGetTitleQuery(dashboardId!, { skip: !dashboardId });
  const { data: bcIdData } = useGetParentBcIdQuery(dashboardId!, {
    skip: !dashboardId || !shouldLoadGenericSummary,
  });
  const { data: timeTooltipData } = useGetTimeTooltipQuery(dashboardId!, { skip: !dashboardId });

  const onChangeWeek = (week: { firstDay: Date; lastDay: Date }) => {
    setDateRange(week);
  };

  const onChangeMonth = (date: Date) => {
    setDateRange({
      firstDay: date,
      lastDay: new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999),
    });
  };

  const onClientChange = (value: number | undefined) => {
    setSelectedClientId(value);
  };

  const onSummaryViewChange = (value: string | undefined) => {
    if (value != null && isDateRangeMode(value)) {
      setSelectedDateRangeMode(value);
    }
  };

  const employeeRecordsCount = employeeRecordsCountData ?? 5;
  const projectsRecordsCount = projectsRecordsCountData ?? 5;
  const hourlyTableWorkLogLink = hourlyTableWorkLogLinkData ?? '';
  const clientRelationStatsRoute = clientRelationStatsRouteData;
  const title = titleData ?? '';
  const bcId = bcIdData ?? 0;
  const timeTooltipTemplate = timeTooltipData ?? '';

  const loading = {
    totals: isLoadingTotals || isFetchingTotals,
    employees: isLoadingEmployees || isFetchingEmployees,
    projects: isLoadingProjects || isFetchingProjects,
    trackedTime: isLoadingTrackedTime || isFetchingTrackedTime,
  };

  const dateFilters: IFilter[] = useMemo(() => {
    return filterService.getDateRangeFilters(dateRange.firstDay, dateRange.lastDay);
  }, [dateRange.firstDay, dateRange.lastDay]);

  const clientOptions = useMemo(
    () =>
      summaryClients.map((client) => ({
        value: client.Id,
        label: client.Name,
      })),
    [summaryClients]
  );

  const currencyViews = useMemo(
    () =>
      [
        usdCurrency && {
          currency: usdCurrency,
          employees: topEmployees?.Usd ?? [],
          projects: topProjects?.Usd ?? [],
          hourlyEmployees: employeeTrackedTime?.Usd ?? [],
          totals: totalTrackedTime?.Usd,
        },
        eurCurrency && {
          currency: eurCurrency,
          employees: topEmployees?.Eur ?? [],
          projects: topProjects?.Eur ?? [],
          hourlyEmployees: employeeTrackedTime?.Eur ?? [],
          totals: totalTrackedTime?.Eur,
        },
      ].filter(Boolean) as SummaryCurrencyView[],
    [employeeTrackedTime, eurCurrency, topEmployees, topProjects, totalTrackedTime, usdCurrency]
  );

  const clientCurrencyView = useMemo(
    () =>
      currencyViews.reduce<SummaryCurrencyView | undefined>((selected, view) => {
        if (!selected) {
          return view;
        }

        return getCurrencyViewActivityScore(view) > getCurrencyViewActivityScore(selected)
          ? view
          : selected;
      }, undefined),
    [currencyViews]
  );

  const renderSummaryContent = (view: SummaryCurrencyView) => {
    const currencySymbol = view.currency.Symbol || view.currency.Code || '';
    const totalsBreakdown = buildTotalsBreakdown(view.totals);
    const hourlyDates = buildTrackedDates(view.hourlyEmployees);

    return (
      <>
        <div className="topsContainer">
          <TopCard
            isLoading={loading.totals}
            Data={totalsBreakdown}
            Filters={dateFilters}
            WorkLogLink={hourlyTableWorkLogLink}
            Title="Totals"
            Footer={() => (
              <Totals
                Title=""
                Data={view.totals?.TotalPrice ?? 0}
                showCurrency={showPrices}
                currencySymbol={currencySymbol}
              />
            )}
            DateRangeMode={dateRangeMode ?? ''}
            RecordsCount={-1}
            isLink={false}
            showPrices={showPrices}
            currencySymbol={currencySymbol}
          />
          <TopCard
            isLoading={loading.employees}
            Data={view.employees}
            Filters={dateFilters}
            WorkLogLink={hourlyTableWorkLogLink}
            Title={`Top ${employeeRecordsCount} employees`}
            openSelectedFilterHandler={(employee) => {
              if (!dateRangeMode) {
                return;
              }

              openEmployeeRelationStatsPage({
                employee,
                dateFilters,
                dateRangeMode,
                clientRelationStatsRoute,
                bcId,
              });
            }}
            Footer={(setIsExpanded) => (
              <MoreButton onClick={(newValue: boolean) => setIsExpanded(newValue)} />
            )}
            DateRangeMode={dateRangeMode ?? ''}
            RecordsCount={employeeRecordsCount}
            FilterDataField={DateFieldNames.Employee}
            isLink={true}
            showPrices={showPrices}
            currencySymbol={currencySymbol}
          />
          <TopCard
            isLoading={loading.projects}
            Data={view.projects}
            Filters={dateFilters}
            WorkLogLink={hourlyTableWorkLogLink}
            Title={`Top ${projectsRecordsCount} projects`}
            Footer={(setIsExpanded) => (
              <MoreButton onClick={(newValue: boolean) => setIsExpanded(newValue)} />
            )}
            DateRangeMode={dateRangeMode ?? ''}
            RecordsCount={projectsRecordsCount}
            FilterDataField={DateFieldNames.Project}
            isLink={true}
            showPrices={showPrices}
            currencySymbol={currencySymbol}
          />
        </div>
        <div className="summary-hourly-container">
          <TrackedTimeTable
            isLoading={loading.trackedTime}
            Data={view.hourlyEmployees}
            Dates={hourlyDates}
            Filters={dateFilters}
            WorkLogLink={hourlyTableWorkLogLink}
            ClientRelationStatsRoute={clientRelationStatsRoute}
            DateRangeMode={dateRangeMode}
            ParentBcId={bcId}
            showPrices={showPrices}
            currencySymbol={currencySymbol}
            dashboardTotalPrice={view.totals?.TotalPrice}
          />
        </div>
      </>
    );
  };

  const renderCurrencyBlock = (view: SummaryCurrencyView, defaultExpanded = true) => (
    <SummaryCurrencySection
      key={view.currency.Code}
      title={view.currency.Code}
      defaultExpanded={defaultExpanded}
    >
      {renderSummaryContent(view)}
    </SummaryCurrencySection>
  );

  return (
    <div className="summary-page">
      <h3 className="summary-title">{title}</h3>
      <div className="summary-container">
        <div className="summary-date-picker">
          <div className="date-picker">
            {!isFinanceView && dateRangeMode === DateRangeModeEnum.Week && (
              <WeekPicker
                disabled={false}
                key={`week-picker-${selectedClientId ?? 'all'}-${clientCurrencyView?.currency.Code ?? 'none'}`}
                onChange={onChangeWeek}
                useLocalTime={useLocalTime}
                clientId={selectedClientId}
                showTrackedTimePreview={!showAdminFilters}
                currencySymbol={
                  clientCurrencyView?.currency.Symbol || clientCurrencyView?.currency.Code || ''
                }
                currencyCode={clientCurrencyView?.currency.Code}
              />
            )}
            {(isFinanceView || dateRangeMode === DateRangeModeEnum.Month) && (
              <MonthPicker
                disabled={false}
                key={`month-picker-${selectedClientId ?? 'all'}-${clientCurrencyView?.currency.Code ?? 'none'}`}
                onChange={onChangeMonth}
                useLocalTime={useLocalTime}
                clientId={selectedClientId}
                showTrackedTimePreview={!showAdminFilters}
                currencySymbol={
                  clientCurrencyView?.currency.Symbol || clientCurrencyView?.currency.Code || ''
                }
                currencyCode={clientCurrencyView?.currency.Code}
              />
            )}
            <UTCTooltip template={timeTooltipTemplate} />
          </div>
          <div className="summary-filters">
            {showAdminFilters && (
              <>
                {!isFinanceView && (
                  <div className="summary-project-picker">
                    <CustomDropdown
                      id="summary-view-select"
                      label="View"
                      value={dateRangeMode || undefined}
                      onChange={onSummaryViewChange}
                      options={summaryViewOptions}
                      searchable={false}
                      showAllOption={false}
                    />
                  </div>
                )}
                {isMonthlyClientFinanceEnabled && (
                  <div className="summary-project-picker">
                    <label className="summary-finance-view-toggle">
                      <input
                        type="checkbox"
                        checked={isFinanceView}
                        onChange={(event) => onFinanceViewChange(event.target.checked)}
                      />
                      Finance view
                    </label>
                  </div>
                )}
                {summaryClients.length > 0 && (
                  <div className="summary-project-picker">
                    <CustomDropdown
                      id="summary-client-select"
                      label="Client"
                      value={selectedClientId}
                      onChange={onClientChange}
                      options={clientOptions}
                      allOptionLabel="All clients"
                      searchable={true}
                      searchPlaceholder="Search client..."
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {isFinanceView ? (
          <MonthlyClientFinanceDashboard
            data={monthlyClientFinance}
            isLoading={isLoadingMonthlyClientFinance || isFetchingMonthlyClientFinance}
          />
        ) : showAdminFilters ? (
          <>
            {currencyViews.map((view) => renderCurrencyBlock(view))}

            <SummaryFooter footer={totalTrackedTime?.Footer} />
          </>
        ) : (
          clientCurrencyView && renderSummaryContent(clientCurrencyView)
        )}
      </div>
    </div>
  );
};

export default Summary;
